import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './boot-screen.css';

const MEDIA = {
  intro: '/assets/ops-boot-r92-intro.mp4',
  idle: '/assets/ops-boot-r57-idle.mp4',
  poster: '/assets/ops-boot-r57-poster.webp',
};
type Stage = 'intro' | 'idle' | 'still';
const VIDEO_RATIO = 16 / 9;

// WHY R58: App alone owns session/board readiness and unmounts this component.
// Media end changes decoration only, never readiness, auth, retries or a minimum wait.
// Human decision: this same cube motion also runs with OS reduced motion enabled.
// Each mount plays the entrance at most once; only the quiet idle can loop.
export function BootScreen({ label = 'Opening your production desk.' }: { label?: string }) {
  const [stage, setStage] = useState<Stage>('intro');
  const [posterFailed, setPosterFailed] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const visualStage = useRef<HTMLDivElement>(null);
  const caption = useRef<HTMLDivElement>(null);
  const mounted = useRef(false);
  const introStarted = useRef(new WeakSet<HTMLVideoElement>());
  const coverScale = useRef(1);
  const restScale = useRef(1);
  const restShiftY = useRef(0);
  const stageWidth = useRef(0);
  const frameRequest = useRef<number | null>(null);
  const frameKind = useRef<'video' | 'animation' | null>(null);
  const introPlaying = useRef(false);

  // WHY: cover the entire opening viewport so flying blocks grow past its edges,
  // then fit the complete cube/check above the caption. A fixed cover crop cuts
  // the final cube on tall/ultrawide screens; fixed contain boxes in the opening.
  // Media timestamps keep fit and shake tied to playback through stalls,
  // background tabs and resize, never to page-load or wall-clock timers.
  const paintIntro = (current: HTMLVideoElement, time: number, playing: boolean) => {
    if (stage !== 'intro') return;
    const phase = Math.max(0, Math.min(1, (time - 0.48) / 0.82));
    const ease = phase * phase * (3 - 2 * phase);
    const opening = playing && time >= 0.02 && time <= 0.30
      ? Math.sin(Math.PI * (time - 0.02) / 0.28) ** 2 : 0;
    const impact = playing && time >= 0.92 && time <= 1.20
      ? Math.sin(Math.PI * (time - 0.92) / 0.28) ** 2 : 0;
    const scale = (coverScale.current + (restScale.current - coverScale.current) * ease)
      * (1 + 0.04 * opening + 0.025 * impact);
    const tremor = opening + impact * 0.45;
    const x = tremor * Math.min(8, Math.max(3, stageWidth.current * 0.005)) * Math.sin(time * 128);
    const y = restShiftY.current * ease + tremor * 4 * Math.sin(time * 173);
    current.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${scale.toFixed(5)})`;
  };
  const stopFrames = (current: HTMLVideoElement) => {
    if (frameRequest.current !== null) {
      if (frameKind.current === 'video') current.cancelVideoFrameCallback(frameRequest.current);
      else cancelAnimationFrame(frameRequest.current);
    }
    frameRequest.current = null;
    frameKind.current = null;
    introPlaying.current = false;
    paintIntro(current, current.currentTime, false);
  };
  const watchFrames = (current: HTMLVideoElement) => {
    if (stage !== 'intro' || frameRequest.current !== null) return;
    const tick = (time: number) => {
      frameRequest.current = null;
      if (video.current !== current || !introPlaying.current) return;
      paintIntro(current, time, true);
      watchFrames(current);
    };
    if (current.requestVideoFrameCallback) {
      frameKind.current = 'video';
      frameRequest.current = current.requestVideoFrameCallback((_now, frame) => tick(frame.mediaTime));
    } else {
      frameKind.current = 'animation';
      frameRequest.current = requestAnimationFrame(() => tick(current.currentTime));
    }
  };
  useLayoutEffect(() => {
    const element = visualStage.current;
    if (!element) return;
    const measure = () => {
      const width = element.clientWidth;
      const height = element.clientHeight;
      if (!width || !height) return;
      stageWidth.current = width;
      const ratio = width / height;
      coverScale.current = Math.max(ratio / VIDEO_RATIO, VIDEO_RATIO / ratio);
      const mediaWidth = Math.min(width, height * VIDEO_RATIO);
      const mediaHeight = mediaWidth / VIDEO_RATIO;
      const mediaLeft = (width - mediaWidth) / 2;
      const mediaTop = (height - mediaHeight) / 2;
      // Poster cube ≈x540..1360/y100..970. Expand it for idle parallax and glow.
      const cubeLeft = mediaLeft + mediaWidth * 516 / 1920;
      const cubeRight = mediaLeft + mediaWidth * 1384 / 1920;
      const cubeTop = mediaTop + mediaHeight * 76 / 1080;
      const cubeBottom = mediaTop + mediaHeight * 994 / 1080;
      const sideInset = Math.min(24, Math.max(8, width * 0.02));
      const maxScaleX = Math.min(
        (width / 2 - sideInset) / (width / 2 - cubeLeft),
        (width / 2 - sideInset) / (cubeRight - width / 2),
      );
      const stageBox = element.getBoundingClientRect();
      const captionBox = caption.current?.getBoundingClientRect();
      const captionReserve = captionBox
        ? captionBox.height + Math.max(0, stageBox.bottom - captionBox.bottom) + 12 : 112;
      const safeTop = Math.min(24, Math.max(8, height * 0.02));
      const safeBottom = Math.max(safeTop + 1, height - captionReserve);
      const maxScaleY = (safeBottom - safeTop) / (cubeBottom - cubeTop);
      restScale.current = Math.max(0.01, Math.min(maxScaleX, maxScaleY));
      restShiftY.current = (safeTop + safeBottom) / 2
        - (height / 2 + restScale.current * ((cubeTop + cubeBottom) / 2 - height / 2));
      element.style.setProperty('--boot-rest-scale', restScale.current.toFixed(5));
      element.style.setProperty('--boot-rest-y', `${restShiftY.current.toFixed(2)}px`);
      if (stage === 'intro' && video.current) paintIntro(video.current, video.current.currentTime, introPlaying.current);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (caption.current) observer.observe(caption.current);
    return () => observer.disconnect();
  }, [stage]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    const current = video.current;
    if (current && !current.hasAttribute('src')) { current.src = stage === 'intro' ? MEDIA.intro : MEDIA.idle; current.load(); }
    if (current) { setPlaybackRate(current); if (current.readyState >= 1) startIntro(current); }
    return () => {
      // Release decode/network work on readiness, failure or unmount.
      if (current) { stopFrames(current); introStarted.current.delete(current); current.pause(); current.removeAttribute('src'); current.load(); }
    };
  }, [stage]);
  // Human R92: preserve every frame from the beginning; accelerate the approach,
  // preserve the check/impact, then ease into slow parallax. Never flash the final
  // poster before intro decode: that looked like the ending followed by a restart.
  // Seek once per video/source, never again on buffering canplay events.
  // Cleanup clears the marker because StrictMode reloads the same element.
  const startIntro = (current: HTMLVideoElement) => {
    if (stage === 'intro' && !introStarted.current.has(current)) {
      current.currentTime = 0; introStarted.current.add(current);
    }
  };
  const setPlaybackRate = (current: HTMLVideoElement) => {
    // Ramp is encoded in the media timestamps: hidden-tab scheduling cannot skip it.
    const rate = stage === 'intro' ? 1 : 0.7;
    current.defaultPlaybackRate = rate; current.playbackRate = rate;
  };
  const failed = () => { if (mounted.current) setStage('still'); };
  return <main className="ops-boot" aria-busy="true" data-boot-stage={stage}>
    <div className="ops-boot__identity">
      <div ref={visualStage} className="ops-boot__stage" aria-hidden="true">
        {stage !== 'intro' && !posterFailed ? <img className="ops-boot__poster" src={MEDIA.poster} alt="" onError={() => setPosterFailed(true)} /> : null}
        {stage !== 'still' ? <video key={stage} ref={video} className="ops-boot__video" src={stage === 'intro' ? MEDIA.intro : MEDIA.idle} poster={stage === 'intro' ? undefined : MEDIA.poster} autoPlay muted playsInline loop={stage === 'idle'} preload="auto" tabIndex={-1}
          onLoadedMetadata={(event) => { setPlaybackRate(event.currentTarget); startIntro(event.currentTarget); }}
          onCanPlay={(event) => { const current = event.currentTarget; setPlaybackRate(current); startIntro(current); void current.play().catch(() => { if (mounted.current && video.current === current) failed(); }); }}
          onPlaying={(event) => { const current = event.currentTarget; if (stage === 'intro') { introPlaying.current = true; paintIntro(current, current.currentTime, true); watchFrames(current); } }}
          onWaiting={(event) => { if (stage === 'intro') stopFrames(event.currentTarget); }}
          onPause={(event) => { if (stage === 'intro') stopFrames(event.currentTarget); }}
          onError={failed} onEnded={() => { if (stage === 'intro' && mounted.current) setStage('idle'); }} /> : null}
      </div>
      <div ref={caption} className="ops-boot__caption">
        <h1 className="ops-boot__name">Business Ops</h1>
        <p className="ops-boot__status" role="status">{label}</p>
      </div>
    </div>
  </main>;
}
